(() => {
  const MODAL_CONTENT_ID = 'quick-add-modal-content';

  function getModalContent() {
    return document.getElementById(MODAL_CONTENT_ID);
  }

  function getRoot(element = null) {
    if (element instanceof Element) {
      const root = element.closest('[data-eth-pdp]');

      if (root) {
        return root;
      }
    }

    return getModalContent()?.querySelector('[data-eth-pdp]') || null;
  }

  function parseVariants(root) {
    const jsonElement = root.querySelector('[data-variant-json]');

    if (!jsonElement) {
      return [];
    }

    try {
      return JSON.parse(jsonElement.textContent.trim());
    } catch (error) {
      console.error('[Eth Quick Add] Variant JSON error:', error);
      return [];
    }
  }

  function formatMoney(cents) {
    const value = Number(cents || 0) / 100;

    return '$' + (
      Number.isInteger(value)
        ? value.toString()
        : value.toFixed(2)
    );
  }

  function getSelectedOptions(root) {
    const selectedOptions = [];

    root.querySelectorAll('[data-option-group]').forEach((group) => {
      const optionIndex = Number(
        group.getAttribute('data-option-index')
      );

      const activeButton =
        group.querySelector(
          '[data-option-button].is-active:not([hidden])'
        ) ||
        group.querySelector(
          '[data-option-button].is-active'
        );

      if (activeButton) {
        selectedOptions[optionIndex] =
          activeButton.getAttribute('data-option-value');
      }
    });

    return selectedOptions;
  }

  function findVariant(variants, selectedOptions) {
    return variants.find((variant) => {
      return variant.options.every((optionValue, optionIndex) => {
        return optionValue === selectedOptions[optionIndex];
      });
    });
  }

  function isPreorderVariant(variant) {
    return Boolean(
      variant &&
      Number(variant.inventory_quantity) <= 0 &&
      variant.inventory_policy === 'continue'
    );
  }

  function updateLabels(root, selectedOptions) {
    root
      .querySelectorAll('[data-selected-option-label]')
      .forEach((label) => {
        const optionIndex = Number(
          label.getAttribute('data-selected-option-label')
        );

        if (selectedOptions[optionIndex] !== undefined) {
          label.textContent = selectedOptions[optionIndex];
        }
      });
  }

  function updateVariantImage(root, variant) {
    if (!variant?.featured_image) {
      return;
    }

    const firstImage = root.querySelector(
      '.eth-pdp__gallery-image'
    );

    if (!firstImage) {
      return;
    }

    firstImage.removeAttribute('srcset');
    firstImage.removeAttribute('sizes');
    firstImage.src = variant.featured_image;
    firstImage.loading = 'eager';
  }

  function updateVariantState(root, updateImage = false) {
    const variants = parseVariants(root);

    if (!variants.length) {
      return;
    }

    const selectedOptions = getSelectedOptions(root);
    const selectedVariant = findVariant(
      variants,
      selectedOptions
    );

    updateLabels(root, selectedOptions);

    if (!selectedVariant) {
      return;
    }

    const hiddenVariantInput = root.querySelector(
      '[data-variant-id-input]'
    );

    const priceElement = root.querySelector('[data-price]');
    const comparePriceElement = root.querySelector(
      '[data-compare-price]'
    );

    const discountElement = root.querySelector(
      '[data-discount]'
    );

    const addToCartButton = root.querySelector(
      '[data-add-to-cart]'
    );

    const addToCartText = root.querySelector(
      '[data-add-to-cart-text]'
    );

    const preorderInput = root.querySelector(
      '[data-preorder-property]'
    );

    const preorderNote = root.querySelector(
      '[data-preorder-note]'
    );

    if (hiddenVariantInput) {
      hiddenVariantInput.value = selectedVariant.id;
    }

    if (priceElement) {
      priceElement.textContent = formatMoney(
        selectedVariant.price
      );
    }

    const hasDiscount =
      Number(selectedVariant.compare_at_price) >
      Number(selectedVariant.price);

    if (comparePriceElement) {
      if (hasDiscount) {
        comparePriceElement.textContent = formatMoney(
          selectedVariant.compare_at_price
        );

        comparePriceElement.classList.remove('is-hidden');
      } else {
        comparePriceElement.textContent = '';
        comparePriceElement.classList.add('is-hidden');
      }
    }

    if (discountElement) {
      if (hasDiscount) {
        const discount = Math.round(
          (
            (
              Number(selectedVariant.compare_at_price) -
              Number(selectedVariant.price)
            ) /
            Number(selectedVariant.compare_at_price)
          ) * 100
        );

        discountElement.textContent = `${discount}% OFF`;
        discountElement.classList.remove('is-hidden');
      } else {
        discountElement.textContent = '';
        discountElement.classList.add('is-hidden');
      }
    }

    const isPreorder = isPreorderVariant(selectedVariant);

    if (preorderInput) {
      preorderInput.disabled = !isPreorder;
    }

    if (preorderNote) {
      preorderNote.classList.toggle(
        'is-hidden',
        !isPreorder
      );
    }

    if (addToCartButton && addToCartText) {
      if (selectedVariant.available) {
        addToCartButton.disabled = false;
        addToCartText.textContent = isPreorder
          ? 'Pre-order'
          : 'Add to Cart';
      } else {
        addToCartButton.disabled = true;
        addToCartText.textContent = 'Sold Out';
      }
    }

    if (updateImage) {
      updateVariantImage(root, selectedVariant);
    }
  }

  function forceModalImages(root) {
    const images = root.querySelectorAll(
      '.eth-pdp__gallery-image'
    );

    images.forEach((image, index) => {
      if (image.dataset.ethQuickAddImageReady === 'true') {
        return;
      }

      const newImage = image.cloneNode(true);

      newImage.dataset.ethQuickAddImageReady = 'true';
      newImage.removeAttribute('loading');
      newImage.loading = 'eager';

      if (index === 0) {
        newImage.fetchPriority = 'high';
      }

      image.replaceWith(newImage);
    });
  }

  function closeSizeMenus(root) {
    root
      .querySelectorAll('[data-size-system-dropdown]')
      .forEach((dropdown) => {
        dropdown.classList.remove('is-open');

        const menu = dropdown.querySelector(
          '[data-size-system-menu]'
        );

        if (menu) {
          menu.hidden = true;
        }
      });
  }

  function selectSizeSystem(picker, systemValue) {
    const currentLabel = picker.querySelector(
      '[data-size-system-current]'
    );

    const sizeButtons = Array.from(
      picker.querySelectorAll(
        '[data-option-button][data-size-system]'
      )
    );

    const systemOptions = Array.from(
      picker.querySelectorAll(
        '[data-size-system-option]'
      )
    );

    let firstVisibleButton = null;
    let activeButtonIsVisible = false;

    sizeButtons.forEach((button) => {
      const isVisible =
        button.getAttribute('data-size-system') === systemValue;

      button.hidden = !isVisible;

      if (isVisible && !firstVisibleButton) {
        firstVisibleButton = button;
      }

      if (isVisible && button.classList.contains('is-active')) {
        activeButtonIsVisible = true;
      }
    });

    systemOptions.forEach((option) => {
      option.classList.toggle(
        'is-active',
        option.getAttribute('data-size-system-value') ===
          systemValue
      );
    });

    if (currentLabel) {
      currentLabel.textContent = systemValue;
    }

    if (!activeButtonIsVisible && firstVisibleButton) {
      sizeButtons.forEach((button) => {
        button.classList.remove('is-active');
      });

      firstVisibleButton.classList.add('is-active');
    }
  }

  function hydrateModal() {
    const root = getRoot();

    if (!root) {
      return;
    }

    forceModalImages(root);

    root.querySelectorAll('[data-size-picker]').forEach((picker) => {
      const selectedSystem = picker.querySelector(
        '[data-size-system-option].is-active'
      );

      const firstSystem = picker.querySelector(
        '[data-size-system-option]'
      );

      const systemValue =
        selectedSystem?.getAttribute(
          'data-size-system-value'
        ) ||
        firstSystem?.getAttribute(
          'data-size-system-value'
        );

      if (systemValue) {
        selectSizeSystem(picker, systemValue);
      }
    });

    updateVariantState(root, false);
  }

  function bindModalEvents() {
    const modalContent = getModalContent();

    if (!modalContent) {
      return;
    }

    if (modalContent.dataset.ethQuickAddBound === 'true') {
      return;
    }

    modalContent.dataset.ethQuickAddBound = 'true';

    modalContent.addEventListener('click', (event) => {
      const target = event.target;

      if (!(target instanceof Element)) {
        return;
      }

      const root = getRoot(target);

      if (!root) {
        return;
      }

      const minusButton = target.closest('[data-qty-minus]');

      if (minusButton) {
        event.preventDefault();

        const quantityInput = root.querySelector(
          '[data-qty-input]'
        );

        if (quantityInput) {
          const currentValue = parseInt(
            quantityInput.value || '1',
            10
          );

          quantityInput.value = Math.max(
            1,
            currentValue - 1
          );

          quantityInput.dispatchEvent(
            new Event('change', { bubbles: true })
          );
        }

        return;
      }

      const plusButton = target.closest('[data-qty-plus]');

      if (plusButton) {
        event.preventDefault();

        const quantityInput = root.querySelector(
          '[data-qty-input]'
        );

        if (quantityInput) {
          const currentValue = parseInt(
            quantityInput.value || '1',
            10
          );

          quantityInput.value = currentValue + 1;

          quantityInput.dispatchEvent(
            new Event('change', { bubbles: true })
          );
        }

        return;
      }

      const sizeTrigger = target.closest(
        '[data-size-system-trigger]'
      );

      if (sizeTrigger) {
        event.preventDefault();

        const dropdown = sizeTrigger.closest(
          '[data-size-system-dropdown]'
        );

        const menu = dropdown?.querySelector(
          '[data-size-system-menu]'
        );

        if (!dropdown || !menu) {
          return;
        }

        const shouldOpen = menu.hidden;

        closeSizeMenus(root);

        dropdown.classList.toggle('is-open', shouldOpen);
        menu.hidden = !shouldOpen;

        return;
      }

      const sizeSystemOption = target.closest(
        '[data-size-system-option]'
      );

      if (sizeSystemOption) {
        event.preventDefault();

        const picker = sizeSystemOption.closest(
          '[data-size-picker]'
        );

        const systemValue =
          sizeSystemOption.getAttribute(
            'data-size-system-value'
          );

        if (picker && systemValue) {
          selectSizeSystem(picker, systemValue);
          closeSizeMenus(root);
          updateVariantState(root, false);
        }

        return;
      }

      const optionButton = target.closest(
        '[data-option-button]'
      );

      if (optionButton) {
        event.preventDefault();

        const optionIndex =
          optionButton.getAttribute('data-option-index');

        root
          .querySelectorAll(
            `[data-option-button][data-option-index="${optionIndex}"]`
          )
          .forEach((button) => {
            button.classList.remove('is-active');
          });

        optionButton.classList.add('is-active');

        updateVariantState(
          root,
          !optionButton.classList.contains(
            'eth-pdp__size-button'
          )
        );
      }
    });

    document.addEventListener('click', (event) => {
      const root = getRoot();

      if (!root) {
        return;
      }

      if (
        event.target instanceof Element &&
        event.target.closest('[data-size-system-dropdown]')
      ) {
        return;
      }

      closeSizeMenus(root);
    });

    const observer = new MutationObserver(() => {
      window.requestAnimationFrame(hydrateModal);
    });

    observer.observe(modalContent, {
      childList: true,
      subtree: true
    });

    hydrateModal();
  }

  function boot() {
    bindModalEvents();
  }

  if (document.readyState === 'loading') {
    document.addEventListener(
      'DOMContentLoaded',
      boot,
      { once: true }
    );
  } else {
    boot();
  }
})();